import React, { useEffect, useRef, useState } from 'react';

const PATHING_LIGHT_COLOUR = '#ffe072';
const PATHING_LIGHT_REACH_METRES = 4;
const PATHING_LIGHT_INTENSITY = 1.0;
const PATHING_LIGHT_TYPE = 'flame';
const PATHING_LIGHT_FLICKER = 1;

export function getShapeType(layer) {
  if (typeof window === 'undefined' || !window.L || !layer) return 'polyline';

  const L = window.L;
  if (layer instanceof L.Circle) return 'circle';
  if (layer instanceof L.Rectangle) return 'rectangle';
  if (layer instanceof L.Polygon) return 'polygon';
  return 'polyline';
}

function approxSameLatLng(a, b) {
  if (!a || !b) return false;
  const eps = 1e-9;
  return Math.abs(a.lat - b.lat) < eps && Math.abs(a.lng - b.lng) < eps;
}

function getLatLngArrayFromLayer(layer, shapeType) {
  if (!layer || typeof window === 'undefined' || !window.L) return [];
  const L = window.L;

  if (shapeType === 'circle') {
    const center = layer.getLatLng();
    const radiusPx = layer.getRadius();
    if (!center || !Number.isFinite(radiusPx) || radiusPx <= 0) return [];

    const segments = 64;
    const pts = [];
    for (let i = 0; i <= segments; i++) {
      const angle = (2 * Math.PI * i) / segments;
      const dx = radiusPx * Math.cos(angle);
      const dy = radiusPx * Math.sin(angle);

      const ll = L.latLng(center.lat + dy, center.lng + dx);
      pts.push(ll);
    }
    return pts;
  }

  let latlngs = layer.getLatLngs();
  if (!latlngs || latlngs.length === 0) return [];

  if (
    Array.isArray(latlngs[0]) &&
    !latlngs[0].lat &&
    !latlngs[0].lng
  ) {
    latlngs = latlngs[0];
  }

  return latlngs.slice();
}

function mapDistanceToMetres(pxDist, metreInPixels) {
  if (!Number.isFinite(pxDist)) return 0;
  if (!Number.isFinite(metreInPixels) || metreInPixels <= 0) return 0;
  return pxDist / metreInPixels;
}

function buildPathInfo(layer, metreInPixels) {
  const shapeType = getShapeType(layer);
  const isClosed =
    shapeType === 'circle' ||
    shapeType === 'rectangle' ||
    shapeType === 'polygon';

  let pts = getLatLngArrayFromLayer(layer, shapeType);
  if (!pts || pts.length < 2) {
    return {
      points: [],
      distances: [],
      totalLength: 0,
      shapeType,
      isClosed,
    };
  }

  if (isClosed) {
    const first = pts[0];
    const last = pts[pts.length - 1];
    if (!approxSameLatLng(first, last)) {
      pts = pts.concat([first]);
    }
  }

  const distances = [0];
  let total = 0;
  for (let i = 1; i < pts.length; i++) {
    const dx = pts[i].lng - pts[i - 1].lng;
    const dy = pts[i].lat - pts[i - 1].lat;
    const dPx = Math.sqrt(dx * dx + dy * dy);

    const dMetres = mapDistanceToMetres(dPx, metreInPixels);
    total += dMetres;
    distances.push(total);
  }

  return {
    points: pts,
    distances,
    totalLength: total,
    shapeType,
    isClosed,
  };
}

export function getPointAtProgress(pathInfo, progress) {
  if (!pathInfo) return null;
  const { points, distances, totalLength, isClosed } = pathInfo;
  if (!points || points.length === 0 || totalLength <= 0) return null;

  let t = progress || 0;
  if (isClosed) {
    t = t - Math.floor(t);
    if (t < 0) t += 1;
  } else {
    t = Math.min(1, Math.max(0, t));
  }

  const targetDist = t * totalLength;

  let low = 0;
  let high = distances.length - 1;
  while (low < high) {
    const mid = Math.floor((low + high) / 2);
    if (distances[mid] < targetDist) low = mid + 1;
    else high = mid;
  }

  const idx = low;
  if (idx <= 0) return points[0];
  if (idx >= distances.length) return points[points.length - 1];

  const d0 = distances[idx - 1];
  const d1 = distances[idx];
  const segLen = d1 - d0 || 1;

  const segT = (targetDist - d0) / segLen;
  const a = points[idx - 1];
  const b = points[idx];

  const lat = a.lat + (b.lat - a.lat) * segT;
  const lng = a.lng + (b.lng - a.lng) * segT;
  return { lat, lng };
}

function stepProgress(pathInfo, progress, deltaSeconds, speed, direction) {
  if (!pathInfo || pathInfo.totalLength <= 0) {
    return { progress, direction };
  }

  const { shapeType, isClosed, totalLength } = pathInfo;
  const isLine = shapeType === 'polyline';
  const wrapMode =
    isLine &&
    (direction === 'wrap-forward' || direction === 'wrap-backward');

  let dirSign = 1;
  if (
    direction === 'reverse' ||
    direction === 'backward' ||
    direction === 'counterclockwise' ||
    direction === 'wrap-backward'
  ) dirSign = -1;

  const speedMps = Math.max(0, Number(speed) || 0);
  const dt = Math.max(0, Number(deltaSeconds) || 0);

  const deltaDist = speedMps * dt * dirSign;
  const delta = deltaDist / totalLength;

  let p = (progress || 0) + delta;

  if (isLine) {
    if (wrapMode) {
      p = p - Math.floor(p);
      if (p < 0) p += 1;
    } else {
      if (p > 1) {
        const over = p - 1;
        p = 1 - over;
        dirSign *= -1;
      } else if (p < 0) {
        const over = -p;
        p = over;
        dirSign *= -1;
      }
    }
  } else if (isClosed) {
    p = p - Math.floor(p);
    if (p < 0) p += 1;
  } else {
    p = Math.min(1, Math.max(0, p));
  }

  let newDirection = direction || 'forward';
  if (direction === 'clockwise' || direction === 'counterclockwise') {
    newDirection = dirSign >= 0 ? 'clockwise' : 'counterclockwise';
  } else if (direction === 'wrap-forward' || direction === 'wrap-backward') {
    newDirection = dirSign >= 0 ? 'wrap-forward' : 'wrap-backward';
  } else if (direction === 'backward' || direction === 'reverse') {
    newDirection = dirSign >= 0 ? 'forward' : 'backward';
  } else {
    newDirection = dirSign >= 0 ? 'forward' : 'reverse';
  }

  return { progress: p, direction: newDirection };
}

export function useShapePathing({
  layer,
  enabled,
  autoMove,
  speed,
  progress,
  direction,
  onConfigChange,
  tokenIconUrl,
  tokenColor,
  metreInPixels,
  tokenRadiusMetres = 1,
  behaviorState,
  onMarkerUpdate,
}) {
  const pathInfoRef = useRef(null);
  const rafRef = useRef(null);
  const lastTimeRef = useRef(null);
  const markerRef = useRef(null);

  const configRef = useRef({
    enabled,
    autoMove,
    speed,
    progress,
    direction,
  });

  const behaviorStateRef = useRef(behaviorState || {});
  const globalTickRef = useRef(0);

  const baseIconSizeRef = useRef(null);
  const baseIconZoomRef = useRef(null);

  const lastPointRef = useRef(null);
  const lastAngleRef = useRef(0);
  const iconScaleRef = useRef(1);

  useEffect(() => {
    configRef.current = { enabled, autoMove, speed, progress, direction };
  }, [enabled, autoMove, speed, progress, direction]);

  useEffect(() => {
    behaviorStateRef.current = behaviorState || {};
  }, [behaviorState]);

  useEffect(() => {
    const broadcastGlobalLight = (light) => {
      if (typeof window === 'undefined') return;
      const bs = behaviorStateRef.current || {};
      const id = bs.id != null ? bs.id : null;

      if (!light) {
        try {
          window.dispatchEvent(
            new CustomEvent('pa-path-light-update', {
              detail: { id, light: null },
            })
          );
        } catch {}
        return;
      }

      globalTickRef.current += 1;
      if (globalTickRef.current % 1 !== 0) return;

      try {
        window.dispatchEvent(
          new CustomEvent('pa-path-light-update', {
            detail: { id, light },
          })
        );
      } catch {}
    };

    const updateIconTransform = () => {
      const marker = markerRef.current;
      if (!marker || !tokenIconUrl) return;
      const el = marker.getElement && marker.getElement();
      if (!el) return;
      const img = el.querySelector('.pa-path-token-icon-img');
      if (!img) return;
      const angleDeg = lastAngleRef.current || 0;
      const scale = iconScaleRef.current || 1;
      img.style.transformOrigin = '50% 50%';
      img.style.transform = `scale(${scale}) rotate(${angleDeg}deg)`;
    };

    const applyIconRotation = (angleDeg) => {
      lastAngleRef.current = angleDeg;
      updateIconTransform();
    };

    const updateCollisionToken = (pt) => {
      if (typeof window === 'undefined') return;
      const bs = behaviorStateRef.current || {};
      const id = bs.id != null ? bs.id : null;
      if (!id) return;

      if (!window.__PA_COLLISION_TOKENS_IMAGE) {
        window.__PA_COLLISION_TOKENS_IMAGE = {};
      }

      if (!pt) {
        try {
          if (
            window.__PA_COLLISION_TOKENS_IMAGE &&
            typeof window.__PA_COLLISION_TOKENS_IMAGE === 'object'
          ) {
            delete window.__PA_COLLISION_TOKENS_IMAGE[id];
          }
        } catch {}

        try {
          if (Array.isArray(window.__PA_TOKENS)) {
            window.__PA_TOKENS = window.__PA_TOKENS.filter(
              (t) => t && t.id !== id
            );
          }
        } catch {}

        return;
      }

      try {
        let metrePx = 0;
        if (Number.isFinite(metreInPixels) && metreInPixels > 0) {
          metrePx = metreInPixels;
        } else if (
          typeof window !== 'undefined' &&
          Number.isFinite(window.__PA_METRE_IN_PIXELS) &&
          window.__PA_METRE_IN_PIXELS > 0
        ) {
          metrePx = window.__PA_METRE_IN_PIXELS;
        }

        const radiusPx = metrePx > 0 ? metrePx * 0.5 : 16;

        // IMPORTANT: your CRS.Simple means lat=Y, lng=X in *map image space*,
        // which is the same space the emitter uses for collision.
        const x = pt.lng;
        const y = pt.lat;

        const tokenObj = {
          id,
          x,
          y,
          radius: radiusPx,
          metre_in_pixels: metrePx || undefined,
        };

        window.__PA_COLLISION_TOKENS_IMAGE[id] = tokenObj;

        const list = Array.isArray(window.__PA_TOKENS)
          ? window.__PA_TOKENS.filter((t) => t && t.id !== id)
          : [];

        list.push(tokenObj);
        window.__PA_TOKENS = list;
      } catch {}
    };

    if (!layer || typeof window === 'undefined' || !window.L) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      lastTimeRef.current = null;
      if (markerRef.current && markerRef.current.remove) {
        const m = markerRef.current;
        if (m._map) m._map.removeLayer(m);
        markerRef.current = null;
      }
      if (typeof onMarkerUpdate === 'function') {
        onMarkerUpdate(null);
      }
      updateCollisionToken(null);
      broadcastGlobalLight(null);
      return;
    }

    const L = window.L;
    let cancelled = false;
    let mapInstance = null;
    let zoomHandler = null;

    function worldRadiusUnits() {
      if (!metreInPixels || metreInPixels <= 0) return 16;
      return metreInPixels * tokenRadiusMetres * 0.5;
    }

    function baseSizePx() {
      const radius = worldRadiusUnits();
      let diameter = radius * 2;

      if (typeof window !== 'undefined') {
        const w = window.innerWidth || 0;
        if (w && w < 768) {
          diameter *= 0.5;
        }
      }

      return diameter;
    }

    const start = () => {
      if (cancelled) return;

      const map = layer._map;
      if (!map) {
        rafRef.current = requestAnimationFrame(start);
        return;
      }

      mapInstance = map;

      const rebuild = () => {
        pathInfoRef.current = buildPathInfo(layer, metreInPixels);
      };

      rebuild();

      zoomHandler = () => {
        rebuild();

        if (
          markerRef.current &&
          tokenIconUrl &&
          map.getZoom &&
          map.getZoomScale &&
          baseIconZoomRef.current != null
        ) {
          const currentZoom = map.getZoom();
          const baseZoom = baseIconZoomRef.current;
          const scale = map.getZoomScale(currentZoom, baseZoom);
          iconScaleRef.current = scale;
          updateIconTransform();
        }
      };

      map.on('zoom', zoomHandler);

      const removeMarker = () => {
        if (markerRef.current && map) {
          map.removeLayer(markerRef.current);
          markerRef.current = null;
        }
      };

      const pushLightUpdate = (pt) => {
        const bs = behaviorStateRef.current || {};

        let tokenIsLight = false;
        if (bs.tokenIsLight !== undefined) {
          tokenIsLight = !!bs.tokenIsLight;
        } else if (bs.IsLight !== undefined) {
          tokenIsLight = !!bs.IsLight;
        } else if (bs.isLight !== undefined) {
          tokenIsLight = !!bs.isLight;
        } else if (bs.light !== undefined) {
          tokenIsLight = !!bs.light;
        }

        if (!pt || !tokenIsLight) {
          if (typeof onMarkerUpdate === 'function') {
            onMarkerUpdate(null);
          }
          broadcastGlobalLight(null);
          return;
        }

        const reach =
          Number.isFinite(bs.reach) && bs.reach > 0
            ? bs.reach
            : PATHING_LIGHT_REACH_METRES;

        const intensity =
          Number.isFinite(bs.intensity) && bs.intensity > 0
            ? bs.intensity
            : PATHING_LIGHT_INTENSITY;

        const colour =
          bs.colour ||
          bs.color ||
          tokenColor ||
          PATHING_LIGHT_COLOUR;

        const reachUnits =
          typeof bs.reach_units === 'string'
            ? bs.reach_units
            : 'metres';

        const lightType =
          bs.light_type ||
          bs.lightType ||
          PATHING_LIGHT_TYPE;

        const flicker =
          bs.flicker ? 1 : PATHING_LIGHT_FLICKER;

        const light = {
          lat: pt.lat,
          lng: pt.lng,
          reach,
          reach_units: reachUnits,
          intensity,
          colour,
          light_type: lightType,
          type: 'light',
          flicker,
          emits_light: 1,
        };

        if (typeof onMarkerUpdate === 'function') {
          onMarkerUpdate(light);
        }

        broadcastGlobalLight(light);
      };

      const ensureMarker = () => {
        if (markerRef.current || !configRef.current.enabled) return;
        const cfg = configRef.current;
        const pt =
          (pathInfoRef.current &&
            getPointAtProgress(pathInfoRef.current, cfg.progress)) ||
          (layer.getBounds && layer.getBounds().getCenter());

        if (!pt) {
          updateCollisionToken(null);
          pushLightUpdate(null);
          return;
        }

        const color =
          (layer?.options?.color || layer?.options?.strokeColor) ||
          tokenColor ||
          '#ffd500';

        const baseSize = baseSizePx();
        const radiusWorld = worldRadiusUnits();

        let marker;
        if (tokenIconUrl) {
          const currentZoom = map.getZoom ? map.getZoom() : 0;
          baseIconZoomRef.current = currentZoom;
          baseIconSizeRef.current = baseSize;
          iconScaleRef.current = 1;

          const icon = L.divIcon({
            className: 'pa-path-token-icon',
            iconSize: [baseSize, baseSize],
            iconAnchor: [baseSize / 2, baseSize / 2],
            html: `
              <div class="pa-path-token-icon-inner" style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;">
                <img 
                  src="${tokenIconUrl}" 
                  class="pa-path-token-icon-img" 
                  style="width:100%;height:100%;display:block;image-rendering:pixelated;" 
                />
              </div>
            `,
          });
          marker = L.marker(pt, { icon }).addTo(map);
        } else {
          marker = L.circle(pt, {
            radius: radiusWorld,
            color,
            weight: 2,
            fillColor: color,
            fillOpacity: 0.95,
          }).addTo(map);
        }

        markerRef.current = marker;
        lastPointRef.current = pt;
        updateCollisionToken(pt);
        pushLightUpdate(pt);

        if (tokenIconUrl) {
          updateIconTransform();
        }
      };

      const updateMarkerAndLight = () => {
        if (!pathInfoRef.current) {
          updateCollisionToken(null);
          pushLightUpdate(null);
          return;
        }

        const cfg = configRef.current;
        const pt = getPointAtProgress(pathInfoRef.current, cfg.progress || 0);

        if (markerRef.current && pt) {
          if (markerRef.current.setStyle) {
            const color =
              (layer?.options?.color || layer?.options?.strokeColor) ||
              tokenColor ||
              '#ffd500';
            markerRef.current.setStyle({
              color,
              fillColor: color,
            });
          }
          markerRef.current.setLatLng(pt);

          if (tokenIconUrl) {
            const last = lastPointRef.current;
            if (last) {
              const dx = pt.lng - last.lng;
              const dy = pt.lat - last.lat;
              const len = Math.sqrt(dx * dx + dy * dy);
              if (len > 1e-6) {
                const angleRad = Math.atan2(-dy, dx);
                const angleDeg = (angleRad * 180) / Math.PI;
                applyIconRotation(angleDeg);
              }
            }
          }
        }

        lastPointRef.current = pt && { lat: pt.lat, lng: pt.lng };

        if (pt) {
          updateCollisionToken(pt);
          pushLightUpdate(pt);
        } else {
          updateCollisionToken(null);
          pushLightUpdate(null);
        }
      };

      function frame(ts) {
        if (cancelled) return;

        const cfg = configRef.current;

        if (!cfg.enabled) {
          removeMarker();
          if (typeof onMarkerUpdate === 'function') {
            onMarkerUpdate(null);
          }
          updateCollisionToken(null);
          broadcastGlobalLight(null);
          lastTimeRef.current = ts;
        } else {
          ensureMarker();
          if (cfg.autoMove && cfg.speed > 0 && pathInfoRef.current) {
            if (lastTimeRef.current == null) {
              lastTimeRef.current = ts;
            } else {
              const dt = (ts - lastTimeRef.current) / 1000;
              lastTimeRef.current = ts;

              const r = stepProgress(
                pathInfoRef.current,
                cfg.progress || 0,
                dt,
                cfg.speed || 0,
                cfg.direction || 'forward'
              );

              if (onConfigChange) {
                onConfigChange(prev => ({
                  ...prev,
                  progress: r.progress,
                  direction: r.direction,
                }));
              }
            }
            updateMarkerAndLight();
          } else {
            updateMarkerAndLight();
            lastTimeRef.current = ts;
          }
        }

        rafRef.current = requestAnimationFrame(frame);
      }

      rafRef.current = requestAnimationFrame(frame);
    };

    start();

    return () => {
      const bs = behaviorStateRef.current || {};
      const id = bs.id != null ? bs.id : null;

      cancelled = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      lastTimeRef.current = null;

      if (mapInstance && zoomHandler) {
        mapInstance.off('zoom', zoomHandler);
      }

      if (markerRef.current) {
        const m = markerRef.current;
        if (m?._map) m._map.removeLayer(m);
        markerRef.current = null;
      }

      if (typeof onMarkerUpdate === 'function') {
        onMarkerUpdate(null);
      }

      if (
        typeof window !== 'undefined' &&
        id &&
        window.__PA_COLLISION_TOKENS_IMAGE &&
        typeof window.__PA_COLLISION_TOKENS_IMAGE === 'object'
      ) {
        try {
          delete window.__PA_COLLISION_TOKENS_IMAGE[id];
        } catch {}
      }

      if (typeof window !== 'undefined' && id && Array.isArray(window.__PA_TOKENS)) {
        try {
          window.__PA_TOKENS = window.__PA_TOKENS.filter(
            (t) => t && t.id !== id
          );
        } catch {}
      }

      broadcastGlobalLight(null);
    };
  }, [
    layer,
    metreInPixels,
    onConfigChange,
    tokenIconUrl,
    tokenColor,
    tokenRadiusMetres,
    onMarkerUpdate,
  ]);

  useEffect(() => {
    if (!enabled && markerRef.current) {
      const m = markerRef.current;
      if (m?._map) m._map.removeLayer(m);
      markerRef.current = null;
    }
    if (!enabled && typeof window !== 'undefined') {
      const bs = behaviorStateRef.current || {};
      const id = bs.id != null ? bs.id : null;
      if (
        id &&
        window.__PA_COLLISION_TOKENS_IMAGE &&
        typeof window.__PA_COLLISION_TOKENS_IMAGE === 'object'
      ) {
        try {
          delete window.__PA_COLLISION_TOKENS_IMAGE[id];
        } catch {}
      }
      if (id && Array.isArray(window.__PA_TOKENS)) {
        try {
          window.__PA_TOKENS = window.__PA_TOKENS.filter(
            (t) => t && t.id !== id
          );
        } catch {}
      }
    }
  }, [enabled]);
}

export function ShapePathingRunner({
  shape,
  layer,
  enabled,
  controls = {},
  showPoiLabels,
  metreInPixels,
}) {
  const safeShape = shape || {};
  const shapeType = getShapeType(layer);
  const isClosedShape =
    (shapeType || "").toLowerCase() === "circle" ||
    (shapeType || "").toLowerCase() === "rectangle" ||
    (shapeType || "").toLowerCase() === "polygon";

  const behaviorType =
    safeShape.behaviorType ??
    safeShape.behavior_type ??
    "zone";

  const isPathingMode = behaviorType === "pathing";

  const globalCtrl =
    (controls && (controls.__all || controls["*"] || controls.all)) || {};
  const perShapeCtrl =
    shape && controls && controls[shape.id]
      ? controls[shape.id]
      : {};

  const ctrlForShape = {
    ...globalCtrl,
    ...perShapeCtrl,
  };

  const hidden = !!ctrlForShape.hidden;

  const [pathConfig, setPathConfig] = useState(() => ({
    progress:
      typeof safeShape.tokenDefaultPosition === "number"
        ? safeShape.tokenDefaultPosition
        : 0,
    speed:
      typeof safeShape.tokenSpeed === "number"
        ? safeShape.tokenSpeed
        : 0.25,
    autoMove:
      safeShape.tokenAutoMove === 1 || safeShape.tokenAutoMove === true,
    direction:
      safeShape.tokenDirection ||
      (isClosedShape ? "clockwise" : "forward"),
  }));

  useEffect(() => {
    if (!shape) return;
    setPathConfig(prev => ({
      ...prev,
      progress:
        typeof safeShape.tokenDefaultPosition === "number"
          ? safeShape.tokenDefaultPosition
          : prev.progress ?? 0,
      speed:
        typeof safeShape.tokenSpeed === "number"
          ? safeShape.tokenSpeed
          : prev.speed ?? 0.25,
      autoMove:
        safeShape.tokenAutoMove === 1 || safeShape.tokenAutoMove === true,
      direction:
        safeShape.tokenDirection ||
        (isClosedShape ? "clockwise" : "forward"),
    }));
  }, [
    shape,
    safeShape.tokenDefaultPosition,
    safeShape.tokenSpeed,
    safeShape.tokenAutoMove,
    safeShape.tokenDirection,
    isClosedShape
  ]);

  const tokenIconUrl =
    safeShape.tokenIconUrl ||
    safeShape.token_icon_url ||
    null;

  const strokeColour =
    safeShape.strokeColor ||
    safeShape.stroke_color ||
    null;

  const tokenColor =
    safeShape.tokenColor ||
    safeShape.token_color ||
    strokeColour ||
    safeShape.fillColor ||
    "#ffe670";

  const paused = !!ctrlForShape.paused;

  const overriddenSpeed =
    typeof ctrlForShape.speed === "number"
      ? ctrlForShape.speed
      : null;

  const overriddenProgress =
    typeof ctrlForShape.progress === "number"
      ? ctrlForShape.progress
      : (typeof ctrlForShape.position === "number"
          ? ctrlForShape.position
          : null);

  useEffect(() => {
    if (overriddenProgress === null) return;
    setPathConfig(prev => ({
      ...prev,
      progress: overriddenProgress,
    }));
  }, [overriddenProgress]);

  const effectiveAutoMove = paused ? false : pathConfig.autoMove;
  const effectiveSpeed =
    overriddenSpeed !== null ? overriddenSpeed : pathConfig.speed;

  const rawLabelFromMeta =
    (layer &&
      layer._shapeMeta &&
      (layer._shapeMeta.label || layer._shapeMeta.name)) ||
    safeShape.label ||
    safeShape.name ||
    (layer &&
      layer.options &&
      (layer.options.label || layer.options.name)) ||
    "";

  const shapeLabel =
    String(rawLabelFromMeta || "").trim() || "Zone";

  const effectiveEnabled = !!enabled && isPathingMode && !hidden;
  const showTokenLabels = effectiveEnabled && !!showPoiLabels;

  const existingState =
    safeShape.behaviorState ||
    safeShape.behavior_state ||
    {};
  const behaviorState = {
    ...existingState,
    id: safeShape.id != null ? safeShape.id : null,
  };

  useShapePathing({
    layer,
    enabled: effectiveEnabled,
    autoMove: effectiveAutoMove,
    speed: effectiveSpeed,
    progress: pathConfig.progress,
    direction: isClosedShape
      ? (pathConfig.direction || "clockwise")
      : (pathConfig.direction || "forward"),
    onConfigChange: setPathConfig,
    tokenIconUrl,
    tokenColor,
    metreInPixels,
    shapeLabel,
    showLabels: showTokenLabels,
    behaviorState,
  });

  return null;
}
